import React, { useState, useEffect, useRef } from 'react'
import { animated, useSpring } from '@react-spring/web'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import { Mode } from '../../models/enums'
import ToggleWithTexts from '../Utils/Inputs/ToggleWithTexts'
import Animation from './Animation'

interface LoaderCallToActionProps {
  showContent?: boolean
  handleValidation: () => void
}

const springEnterDuration = 500
const springLeaveDuration = 1000

const LoaderCallToAction = ({ showContent, handleValidation }: LoaderCallToActionProps): JSX.Element => {
  const dispatch = useDispatch()
  const locale = useSelector((state: RootState) => state.localeReducer.localeForRetorikStudioContent)
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const loaderInformationTexts = useSelector((state: RootState) => state.retorikReducer.configuration.loaderInformationTexts)
  const loaderInformationTextsLocalized = useSelector((state: RootState) => state.retorikReducer.configuration.loaderInformationTextsLocalized)
  const hideRetorikLogo = useSelector((state: RootState) => state.retorikReducer.configuration.hideRetorikLogo)
  const disabledSpeechMode = useSelector((state: RootState) => state.retorikReducer.configuration.disableSpeechMode)
  const disabledTextMode = useSelector((state: RootState) => state.retorikReducer.configuration.disableTextMode)
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const themeColors = useSelector((state: RootState) => state.viewReducer.themeColors)
  const characterLoaded = useSelector((state: RootState) => state.viewReducer.characterLoaded)

  const container = useRef<HTMLDivElement>(null)
  const [userValidation, setUserValidation] = useState<boolean>(false)
  const [hover, setHover] = useState<boolean>(false)
  const animationColor = {
    '--rf-color-loader-animation': themeColors.loader.animation
  } as React.CSSProperties
  const animationTimerRef = useRef<NodeJS.Timeout>()

  const [spring, api] = useSpring(() => ({
    from: {
      opacity: 0
    }
  }))

  useEffect(() => {
    api.start({
      from: {
        opacity: 0
      },
      to: {
        opacity: 1
      },
      config: {
        duration: springEnterDuration
      }
    })

    return () => {
      animationTimerRef?.current && clearTimeout(animationTimerRef.current)
    }
  }, [])

  const handleChange = (modeId: number): void => {
    dispatch(storeActions.speech.setMode(modeId === 1 ? Mode.vocal : Mode.text))
  }

  useEffect(() => {
    if (userValidation && characterLoaded) {
      handleValidation()
      api.start({
        from: {
          opacity: 1
        },
        to: {
          opacity: 0
        },
        config: {
          duration: springLeaveDuration
        }
      })
    }
  }, [userValidation, characterLoaded])

  return (
    <animated.div
      className='rf-h-full rf-w-full rf-flex rf-flex-col rf-items-center rf-animate-loaderFadeIn'
      ref={container}
      style={{
        ...animationColor,
        ...spring
      }}
    >
      {/* Animation + Text */}
      <div className='rf-w-full rf-h-1/2 rf-pb-6 rf-flex rf-flex-col rf-items-center rf-justify-end'>
        {/* Animation */}
        <div className='rf-w-6 rf-h-6 rf-mb-16'>
          <Animation />
        </div>
        {/* Text */}
        {showContent && (
          <div
            className='rf-text-center'
            style={{
              color: themeColors.loader.text
            }}
          >
            {/* First line of informations */}
            {(loaderInformationTexts?.vocal.top && loaderInformationTexts.text.top) ||
            (loaderInformationTextsLocalized?.vocal?.top?.[locale] && loaderInformationTextsLocalized.text?.top?.[locale]) ? (
              <p>
                {mode === Mode.vocal
                  ? loaderInformationTextsLocalized?.vocal?.top?.[locale] || loaderInformationTexts?.vocal?.top
                  : loaderInformationTextsLocalized?.text?.top?.[locale] || loaderInformationTexts?.text?.top}
              </p>
            ) : (
              <p>{mode === Mode.vocal ? translation.loader.loader.optimal : translation.loader.loader.classic}</p>
            )}

            {/* Second line of informations */}
            {(loaderInformationTexts?.vocal?.bottom && loaderInformationTexts.text?.bottom) ||
            (loaderInformationTextsLocalized?.vocal?.bottom?.[locale] && loaderInformationTextsLocalized.text?.bottom?.[locale]) ? (
              <p className='rf-font-bold'>
                {mode === Mode.vocal
                  ? loaderInformationTextsLocalized?.vocal?.bottom?.[locale] || loaderInformationTexts?.vocal?.bottom
                  : loaderInformationTextsLocalized?.text?.bottom?.[locale] || loaderInformationTexts?.text?.bottom}
              </p>
            ) : (
              <p className='rf-font-bold'>{mode === Mode.vocal ? translation.loader.loader.vocal : translation.loader.loader.text}</p>
            )}
          </div>
        )}
      </div>

      {/* Button + Logo */}
      <div className={`rf-w-full rf-h-1/2 rf-flex rf-flex-col ${hideRetorikLogo ? 'rf-justify-start' : 'rf-justify-between'} rf-items-center`}>
        <div className='rf-flex rf-flex-col rf-items-center'>
          {/* Toggle button with both modes */}
          {showContent && (
            <ToggleWithTexts
              texts={{
                left: translation.loader.modes.vocal,
                leftId: 1,
                right: translation.loader.modes.text,
                rightId: 2
              }}
              colors={themeColors.loader.toggle}
              defaultChecked={mode}
              handleChange={handleChange}
              showLeft={!disabledSpeechMode}
              showRight={!disabledTextMode}
            />
          )}

          {/* Validation button or step text */}
          {userValidation ? (
            <React.Fragment />
          ) : (
            <button
              className='rf-font-bold rf-border-2 rf-rounded-lg rf-px-6 rf-py-3 rf-mt-8'
              style={{
                color: hover ? themeColors.loader.button.text.hover : themeColors.loader.button.text.default,
                borderColor: hover ? themeColors.loader.button.border.hover : themeColors.loader.button.border.default,
                background: hover ? themeColors.loader.button.background.hover : themeColors.loader.button.background.default
              }}
              onClick={(): void => setUserValidation(true)}
              onMouseEnter={(): void => setHover(true)}
              onMouseLeave={(): void => setHover(false)}
            >
              {translation.loader.loaderValidation}
            </button>
          )}
        </div>
      </div>
    </animated.div>
  )
}

export default LoaderCallToAction
